import type { RigidSystem } from './jacobian';
import { evaluateRigidSystem } from './jacobian';
import { advanceAlongBranch } from './rigidFoldability';
import { numericalRank } from '@/geometry/linalg';
import { RANK_RELATIVE_TOLERANCE } from '@/constants/numeric';

export interface DofResult {
  variables: number;
  constraintEquations: number;
  independentConstraints: number;
  /** variables - rank(J). */
  estimatedDof: number;
  singularValues: number[];
  conditionNumber: number;
  /**
   * True when the configuration the Jacobian was evaluated at is (numerically)
   * flat. The completely unfolded state is a bifurcation point of the rigid
   * folding variety, so the rank there is generically lower than on the branch,
   * which inflates the DOF count. We therefore evaluate at a perturbed state
   * and say so.
   */
  evaluatedAtPerturbedState: boolean;
  notes: string[];
}

/**
 * Estimate the number of rigid-folding degrees of freedom as
 *
 *     dof = (# fold-angle variables) - rank( dC/drho )
 *
 * The rank is computed from the singular values of the Jacobian with a relative
 * threshold. This is an *estimate*: rank is not a continuous function, so near a
 * bifurcation the answer depends on the threshold. Both the singular-value
 * spectrum and the condition number are returned so the user can judge.
 */
export function estimateDegreesOfFreedom(
  system: RigidSystem,
  rho: Float64Array,
  perturbationDirection?: Float64Array,
): DofResult {
  const notes: string[] = [];
  let evalRho = rho;
  let perturbed = false;

  let mag = 0;
  for (let i = 0; i < rho.length; i++) mag = Math.max(mag, Math.abs(rho[i]));
  if (mag < 1e-6 && system.variableCount > 0) {
    // The flat state is a bifurcation point (see rigidFoldability.ts), so walk a
    // short way along the requested folding branch and measure the rank there.
    const direction = new Float64Array(system.variableCount);
    for (let i = 0; i < direction.length; i++) {
      const d = perturbationDirection?.[i] ?? (i % 2 === 0 ? 1 : -1);
      direction[i] = Math.sign(d) || 1;
    }
    const branch = advanceAlongBranch(system, direction, 0.25);
    if (branch.reached > 1e-4) {
      perturbed = true;
      evalRho = branch.rho;
      notes.push(
        'The flat state is a bifurcation point of the rigid-folding variety, so the rank was measured after folding a short way along the requested branch.',
      );
    } else {
      notes.push(
        'The solver could not leave the flat state along the requested branch; the count below is the first-order (infinitesimal) mobility at the flat state and is usually an over-estimate.',
      );
    }
  }

  if (system.variableCount === 0) {
    notes.push('No foldable interior creases: nothing can move.');
    return {
      variables: 0,
      constraintEquations: system.constraintCount,
      independentConstraints: 0,
      estimatedDof: 0,
      singularValues: [],
      conditionNumber: 0,
      evaluatedAtPerturbedState: perturbed,
      notes,
    };
  }

  if (system.constraintCount === 0) {
    notes.push(
      'No interior vertices, so no loop-closure constraints: every crease folds independently.',
    );
    return {
      variables: system.variableCount,
      constraintEquations: 0,
      independentConstraints: 0,
      estimatedDof: system.variableCount,
      singularValues: [],
      conditionNumber: 0,
      evaluatedAtPerturbedState: perturbed,
      notes,
    };
  }

  const { jacobian } = evaluateRigidSystem(system, evalRho, true);
  const { rank, singularValues, conditionNumber } = numericalRank(
    jacobian,
    RANK_RELATIVE_TOLERANCE,
  );

  const dof = system.variableCount - rank;
  if (!Number.isFinite(conditionNumber)) {
    notes.push('The Jacobian is rank deficient; the estimate is threshold sensitive.');
  }
  if (dof <= 0) {
    notes.push(
      'No continuous rigid folding motion was detected. The pattern may still fold with facet bending - try the compliant solver.',
    );
  }

  return {
    variables: system.variableCount,
    constraintEquations: system.constraintCount,
    independentConstraints: rank,
    estimatedDof: dof,
    singularValues: Array.from(singularValues.slice(0, 24)),
    conditionNumber,
    evaluatedAtPerturbedState: perturbed,
    notes,
  };
}
